/**
 * Title + text banner — line-clamp + Read more / Read less with smooth height transition.
 * Collapse keeps "expanded" until the height animation finishes so clamp does not apply mid-transition.
 */
(function initLvzCategorySeoText() {
	'use strict';

	var SEL_ROOT = '.js-lvz-title-text-banner';
	var SEL_SHELL = '.js-lvz-title-text-banner__shell';
	var SEL_TEXT = '.js-lvz-title-text-banner__text';
	var SEL_TOGGLE = '.js-lvz-title-text-banner__toggle';
	var MQ_DESKTOP = '(min-width: 1024px)';

	/**
	 * @returns {boolean}
	 */
	function prefersReducedMotion() {
		return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	/**
	 * Height of the text block if line-clamp were applied (same rules as SCSS).
	 * Uses an off-screen clone so we never toggle clamp on the live node during measurement.
	 *
	 * @param {HTMLElement} textEl
	 * @returns {number}
	 */
	function measureCollapsedHeight(textEl) {
		var clone = textEl.cloneNode(true);
		var cs = window.getComputedStyle(textEl);
		var width = textEl.offsetWidth;
		var lineClamp = window.matchMedia(MQ_DESKTOP).matches ? '3' : '8';

		clone.style.cssText = [
			'position:absolute',
			'left:-99999px',
			'top:0',
			'visibility:hidden',
			'pointer-events:none',
			'box-sizing:border-box',
			'width:' + width + 'px',
			'display:-webkit-box',
			'-webkit-box-orient:vertical',
			'-webkit-line-clamp:' + lineClamp,
			'overflow:hidden',
			'font-size:' + cs.fontSize,
			'line-height:' + cs.lineHeight,
			'font-family:' + cs.fontFamily,
			'font-weight:' + cs.fontWeight,
			'letter-spacing:' + cs.letterSpacing,
			'word-break:' + cs.wordBreak,
		].join(';');

		document.body.appendChild(clone);
		var h = clone.offsetHeight;
		document.body.removeChild(clone);
		return h;
	}

	/**
	 * @param {HTMLElement} textEl
	 * @returns {boolean}
	 */
	function isTruncated(textEl) {
		if (!textEl) {
			return false;
		}
		return textEl.scrollHeight > textEl.clientHeight + 2;
	}

	/**
	 * @param {HTMLElement} shell
	 * @param {Function} done
	 */
	function onHeightTransitionEnd(shell, done) {
		function handler(e) {
			if (e.propertyName !== 'height') {
				return;
			}
			shell.removeEventListener('transitionend', handler);
			done();
		}
		shell.addEventListener('transitionend', handler);
	}

	/**
	 * @param {HTMLElement} shell
	 * @param {HTMLElement} textEl
	 * @param {HTMLElement} section
	 */
	function expandWithTransition(shell, textEl, section) {
		var collapsedH = shell.offsetHeight;
		shell.style.height = collapsedH + 'px';
		shell.classList.add('lvz-title-text-banner__text-shell--animating');
		// eslint-disable-next-line no-unused-expressions
		section.offsetHeight;

		section.classList.add('lvz-title-text-banner--expanded');
		var fullH = textEl.scrollHeight;

		requestAnimationFrame(function () {
			requestAnimationFrame(function () {
				shell.style.height = fullH + 'px';
			});
		});

		onHeightTransitionEnd(shell, function () {
			shell.style.height = 'auto';
			shell.classList.remove('lvz-title-text-banner__text-shell--animating');
		});
	}

	/**
	 * Shrink shell while content stays full (expanded); remove expanded + clamp only after transition.
	 *
	 * @param {HTMLElement} shell
	 * @param {HTMLElement} textEl
	 * @param {HTMLElement} section
	 */
	function collapseWithTransition(shell, textEl, section) {
		var fullH = textEl.scrollHeight;
		var collapsedH = measureCollapsedHeight(textEl);

		shell.style.height = fullH + 'px';
		shell.classList.add('lvz-title-text-banner__text-shell--animating');
		// eslint-disable-next-line no-unused-expressions
		shell.offsetHeight;

		requestAnimationFrame(function () {
			requestAnimationFrame(function () {
				shell.style.height = collapsedH + 'px';
			});
		});

		onHeightTransitionEnd(shell, function () {
			section.classList.remove('lvz-title-text-banner--expanded');
			shell.style.height = '';
			shell.classList.remove('lvz-title-text-banner__text-shell--animating');
		});
	}

	/**
	 * @param {HTMLElement} section
	 */
	function bindToggle(section) {
		var shell = section.querySelector(SEL_SHELL);
		var textEl = section.querySelector(SEL_TEXT);
		var btn = section.querySelector(SEL_TOGGLE);
		if (!textEl || !btn) {
			return;
		}

		function updateButtonVisibility() {
			requestAnimationFrame(function () {
				if (isTruncated(textEl)) {
					btn.removeAttribute('hidden');
				} else {
					btn.setAttribute('hidden', '');
					section.classList.remove('lvz-title-text-banner--expanded');
					btn.setAttribute('aria-expanded', 'false');
					if (shell) {
						shell.style.height = '';
						shell.classList.remove('lvz-title-text-banner__text-shell--animating');
					}
				}
			});
		}

		btn.addEventListener('click', function onToggleClick() {
			var contentExpanded = section.classList.contains('lvz-title-text-banner--expanded');

			if (prefersReducedMotion() || !shell) {
				var next = !contentExpanded;
				section.classList.toggle('lvz-title-text-banner--expanded', next);
				btn.setAttribute('aria-expanded', next ? 'true' : 'false');
				if (shell && !prefersReducedMotion()) {
					shell.style.height = '';
				}
				return;
			}

			// aria-expanded updates immediately (label + arrow); section--expanded controls clamp / height animation
			if (!contentExpanded) {
				btn.setAttribute('aria-expanded', 'true');
				expandWithTransition(shell, textEl, section);
			} else {
				btn.setAttribute('aria-expanded', 'false');
				collapseWithTransition(shell, textEl, section);
			}
		});

		updateButtonVisibility();

		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(function () {
				updateButtonVisibility();
			});
		}

		requestAnimationFrame(function () {
			requestAnimationFrame(updateButtonVisibility);
		});

		window.addEventListener(
			'resize',
			function () {
				if (!section.classList.contains('lvz-title-text-banner--expanded')) {
					updateButtonVisibility();
				} else if (shell) {
					shell.style.height = 'auto';
				}
			},
			{ passive: true }
		);
	}

	function boot() {
		document.querySelectorAll(SEL_ROOT).forEach(function (section) {
			bindToggle(section);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot);
	} else {
		boot();
	}
})();
